// frontend/src/pages/admin/Sales.tsx
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DefaultLayout from '../../components/layouts/DefaultLayout'
import AdminNav from '../../components/layouts/AdminNav'
import {
  Box,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Button,
  HStack,
  Select,
  Badge,
  Tooltip,
  useToast,
  useBreakpointValue,
  Stack,
  Divider,
  IconButton
} from '@chakra-ui/react'
import { SearchIcon, CopyIcon } from '@chakra-ui/icons'
import { getSalesList } from '../../services/apiService'
import dayjs from 'dayjs'

type SalesQuery = {
  page: number
  pageSize: number
  keyword: string
  dateFrom?: string
  dateTo?: string
}

type SalesItem = {
  id: number
  userId: number
  type: 'BUY_PASS'
  usdtAmount: string | null
  beforeUsdtBalance: string | null
  afterUsdtBalance: string | null
  serviceDate: string | null
  beforeServiceDate: string | null
  afterServiceDate: string | null
  txid: string | null
  createdAt: string | null
  account?: string
  username?: string
  phone?: string
  note1: string | null
  note2: string | null
  note3: string | null
}

const Sales = () => {
  const { t } = useTranslation()
  const toast = useToast()

  // 검색/페이지네이션
  const [keyword, setKeyword] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<SalesItem[]>([])
  const [loading, setLoading] = useState(false)

  // 상단 상태들 아래에 추가
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  // 컴포넌트 내부
  const pageSalesSum = useMemo(() => {
    try {
      return items.reduce((acc, s) => {
        const n = Number(s.usdtAmount ?? 0)
        if (Number.isNaN(n)) return acc
        return acc + Math.abs(n)
      }, 0)
    } catch {
      return 0
    }
  }, [items])

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  const isMobile = useBreakpointValue({ base: true, md: false })

  // ===== 유틸 =====
  const formatDate = (v?: string | null) => {
    if (!v) return '-'
    const d = dayjs(v)
    return d.isValid() ? d.format('YYYY-MM-DD HH:mm:ss') : v
  }

  // 말미 0 제거 + 천단위 콤마
  const formatAmt = (v?: string | null) => {
    if (v == null || v === '') return '-'
    const [int, frac = ''] = String(v).split('.')
    const fracTrimmed = frac.replace(/0+$/, '')
    const intComma = Number(int).toLocaleString()
    return fracTrimmed ? `${intComma}.${fracTrimmed}` : intComma
  }

  // 잔액 증감(후 - 전)
  const calcDelta = (before?: string | null, after?: string | null) => {
    if (!before || !after) return ''
    const a = Number(after)
    const b = Number(before)
    if (Number.isNaN(a) || Number.isNaN(b)) return ''
    const diff = a - b
    const sign = diff > 0 ? '+' : diff < 0 ? '-' : ''
    return `${sign}${formatAmt(String(Math.abs(diff)))}`
  }

  const copy = async (text?: string | null, label = '복사') => {
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      toast({ title: `${label} 완료`, status: 'success', duration: 1200 })
    } catch {
      toast({ title: `${label} 실패`, status: 'error', duration: 1500 })
    }
  }

  // ===== API =====
  const fetchList = async (override?: Partial<SalesQuery>) => {
    try {
      setLoading(true)
      const params = { page, pageSize, keyword, dateFrom, dateTo, ...override }
      const res = await getSalesList(params)
      const payload = res?.data?.data
      setItems(payload?.items ?? [])
      setTotal(payload?.total ?? 0)
      if (typeof payload?.page === 'number') setPage(payload.page)
      if (typeof payload?.pageSize === 'number') setPageSize(payload.pageSize)
    } catch (e: any) {
      toast({
        title: '매출 목록 조회 실패',
        description: e?.message ?? '네트워크 오류가 발생했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize])

  const onSearch = () => {
    setPage(1)
    fetchList({ page: 1 })
  }

  // ===== 모바일 카드 =====
  const MobileCard = ({ s }: { s: SalesItem }) => (
    <Box borderWidth="1px" borderRadius="lg" p={4}>
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontWeight="bold">{s.username || '-'}</Text>
        <Badge colorScheme="purple" variant="subtle">
          이용권 구매
        </Badge>
      </Flex>
      <Text fontSize="sm" color="gray.600">
        @{s.account || '-'}
      </Text>
      <Divider my={3} />

      <Stack spacing={1} fontSize="sm">
        <Flex justify="space-between">
          <Text color="gray.500">결제 USDT</Text>
          <Text fontWeight="semibold">{formatAmt(s.usdtAmount)}</Text>
        </Flex>

        <Flex justify="space-between">
          <Text color="gray.500">잔액 (전 → 후)</Text>
          <Text>
            {formatAmt(s.beforeUsdtBalance)} → {formatAmt(s.afterUsdtBalance)}{' '}
            <Badge ml={2} colorScheme="blue" variant="subtle">
              {calcDelta(s.beforeUsdtBalance, s.afterUsdtBalance)}
            </Badge>
          </Text>
        </Flex>

        <Flex justify="space-between">
          <Text color="gray.500">서비스일 (전 → 후)</Text>
          <Text>
            {formatDate(s.beforeServiceDate)} → {formatDate(s.afterServiceDate)}
          </Text>
        </Flex>

        <Flex justify="space-between">
          <Text color="gray.500">적용 기준일</Text>
          <Text>{formatDate(s.serviceDate)}</Text>
        </Flex>

        <Flex justify="space-between" align="center" gap={2}>
          <Text color="gray.500">TXID</Text>
          <HStack spacing={1} maxW="65%">
            <Tooltip label={s.txid || ''} hasArrow>
              <Text noOfLines={1}>{s.txid || '-'}</Text>
            </Tooltip>
            {s.txid && (
              <IconButton
                aria-label="copy txid"
                size="xs"
                variant="ghost"
                icon={<CopyIcon />}
                onClick={() => copy(s.txid, 'TXID')}
              />
            )}
          </HStack>
        </Flex>

        <Flex justify="space-between">
          <Text color="gray.500">생성일</Text>
          <Text>{formatDate(s.createdAt)}</Text>
        </Flex>
      </Stack>
    </Box>
  )

  return (
    <DefaultLayout>
      <AdminNav />

      <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
        {/* 상단: 제목 + 검색 */}
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          mb={3}
          gap={3}
        >
          <Heading as="h2" size="md">
            매출 관리
          </Heading>

          <HStack spacing={3} align="stretch" wrap="wrap">
            {/* 기존 검색바들 */}
            <InputGroup width={{ base: '100%', md: '320px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={SearchIcon} />
              </InputLeftElement>
              <Input
                placeholder="아이디/이름/연락처/TXID"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch()}
              />
            </InputGroup>

            {/* 기간: created_at */}
            <HStack>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <Text>~</Text>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </HStack>

            <Button onClick={onSearch} minW={{ base: 'auto', md: '70px' }}>
              검색
            </Button>

            {/* 빠른범위(선택) */}
            <HStack>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const today = dayjs().format('YYYY-MM-DD')
                  setDateFrom(today)
                  setDateTo(today)
                  setPage(1)
                  fetchList({ page: 1, dateFrom: today, dateTo: today })
                }}
              >
                오늘
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const to = dayjs().format('YYYY-MM-DD')
                  const from = dayjs().subtract(7, 'day').format('YYYY-MM-DD')
                  setDateFrom(from)
                  setDateTo(to)
                  setPage(1)
                  fetchList({ page: 1, dateFrom: from, dateTo: to })
                }}
              >
                7일
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const to = dayjs().format('YYYY-MM-DD')
                  const from = dayjs().subtract(30, 'day').format('YYYY-MM-DD')
                  setDateFrom(from)
                  setDateTo(to)
                  setPage(1)
                  fetchList({ page: 1, dateFrom: from, dateTo: to })
                }}
              >
                30일
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setDateFrom('')
                  setDateTo('')
                  setPage(1)
                  fetchList({ page: 1, dateFrom: '', dateTo: '' })
                }}
              >
                초기화
              </Button>
            </HStack>

            <Select
              width={{ base: '110px', md: '120px' }}
              value={pageSize}
              onChange={(e) => {
                const next = Number(e.target.value)
                setPageSize(next)
                setPage(1)
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}개씩
                </option>
              ))}
            </Select>
          </HStack>
        </Flex>

        {/* 총 건수/페이지 */}
        <Flex justify="space-between" align="center" mb={2} wrap="wrap" gap={2}>
          <Text color="gray.600" fontSize="sm">
            총 <b>{total.toLocaleString()}</b>건
          </Text>

          <HStack spacing={3}>
            <Text color="gray.500" fontSize="sm">
              페이지 {page} / {totalPages}
            </Text>
            <Badge colorScheme="pink" variant="solid">
              [페이지] 매출 합계: {formatAmt(String(pageSalesSum))}
            </Badge>
          </HStack>
        </Flex>

        {/* 리스트 */}
        {loading ? (
          <Flex align="center" justify="center" py={8} gap={2}>
            <Spinner />
            <Text>불러오는 중…</Text>
          </Flex>
        ) : items.length === 0 ? (
          <Flex align="center" justify="center" py={8}>
            <Text color="gray.500">데이터가 없습니다.</Text>
          </Flex>
        ) : isMobile ? (
          <Stack spacing={3}>
            {items.map((s) => (
              <MobileCard key={s.id} s={s} />
            ))}
          </Stack>
        ) : (
          <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
            <Table size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th>번호</Th>
                  <Th>아이디</Th>
                  <Th>이름</Th>
                  <Th>연락처</Th>
                  <Th isNumeric>결제 USDT</Th>
                  <Th>잔액 (전 → 후)</Th>
                  <Th>서비스일 (전 → 후)</Th>
                  <Th>노트1</Th>
                  <Th>노트2</Th>
                  <Th>노트3</Th>
                  <Th>생성일</Th>
                </Tr>
              </Thead>
              <Tbody>
                {items.map((s) => (
                  <Tr key={s.id}>
                    <Td>{s.id}</Td>
                    <Td>{s.account || '-'}</Td>
                    <Td>{s.username || '-'}</Td>
                    <Td>{s.phone || '-'}</Td>
                    <Td isNumeric>
                      <Badge colorScheme="blue">
                        {formatAmt(s.usdtAmount)}
                      </Badge>
                    </Td>
                    <Td>
                      {formatAmt(s.beforeUsdtBalance)} →{' '}
                      {formatAmt(s.afterUsdtBalance)}
                    </Td>
                    <Td>
                      {formatDate(s.beforeServiceDate)} →{' '}
                      {formatDate(s.afterServiceDate)}
                    </Td>
                    <Td>{s.note1}</Td>
                    <Td>{s.note2}</Td>
                    <Td>{s.note3}</Td>
                    <Td>{formatDate(s.createdAt)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {/* 페이지네이션 */}
        <Flex align="center" justify="center" gap={2} mt={4}>
          <Button
            onClick={() => setPage(1)}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            처음
          </Button>
          <Button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            isDisabled={page <= 1 || loading}
            variant="outline"
            size="sm"
          >
            이전
          </Button>
          <Text fontSize="sm">
            {page} / {totalPages}
          </Text>
          <Button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            다음
          </Button>
          <Button
            onClick={() => setPage(totalPages)}
            isDisabled={page >= totalPages || loading}
            variant="outline"
            size="sm"
          >
            마지막
          </Button>
        </Flex>
      </Box>
    </DefaultLayout>
  )
}

export default Sales
